import React, { useEffect, useState } from 'react';
import Navbar from '../Navbar/Navbar';
import './UserAdmin.css';
import { useTheme } from '../ThemeContext';
import ApiClient from '../../api/apiClient';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';

const UserAdmin = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { users: cachedUsers, refreshUsers } = useData();
  const [formData, setFormData] = useState({ displayName: '', email: '', password: '', confirmPassword: '', role: 'normal' });
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState(null);
  const [pendingRole, setPendingRole] = useState({});
  const selfId = user?._id || user?.id;

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  useEffect(() => {
    setUsers(cachedUsers || []);
  }, [cachedUsers]);

  useEffect(() => {
    if (!status?.message) return undefined;
    const timer = setTimeout(() => setStatus(null), 4500);
    return () => clearTimeout(timer);
  }, [status]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    try {
      const response = await ApiClient.post('/api/users/admin-create', {
        ...formData,
      });
      if (response.data?.success) {
        setStatus({ type: 'success', message: 'User created successfully' });
        setFormData({ displayName: '', email: '', password: '', confirmPassword: '' });
        await refreshUsers();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create user';
      setStatus({ type: 'error', message });
    }
  };

  const handleRoleSelectChange = (id, value) => {
    setPendingRole(prev => ({ ...prev, [id]: value }));
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      await ApiClient.patch(`/api/users/${id}/role`, { role: newRole });
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update role';
      setStatus({ type: 'error', message });
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await ApiClient.delete(`/api/users/${id}`);
      setStatus({ type: 'success', message: 'User deleted' });
      await refreshUsers();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete user';
      setStatus({ type: 'error', message });
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className={`UserAdmin-container ${theme}`}>
        <Navbar />
        <div className="UserAdmin-content page-transition">
          <p>You need admin access to manage users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`UserAdmin-container ${theme}`}>
      <Navbar />
      <div className="UserAdmin-content page-transition">
        <div className="page-shell">
          <header className="useradmin-header">
            <div>
              <h1 className="page-title">Add teammates</h1>
              <p className="useradmin-subtitle">Invite users to your organization ({user?.organization || 'default'}).</p>
            </div>
          </header>

          <div className="useradmin-grid">
            <div className="user-card form-card">
              <h2>Create user</h2>
              <form className="useradmin-form" onSubmit={handleSubmit}>
                <label>
                  Full name
                    <input
                      type="text"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      required
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </label>
                  <label>
                    Password
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                  </label>
                  <label>
                    Confirm password
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                  </label>
                  <label>
                    Account type
                    <select name="role" value={formData.role} onChange={handleChange}>
                      <option value="normal">Normal user</option>
                      <option value="admin">Admin</option>
                    </select>
                  </label>
                  <button type="submit" className="primary-btn">Create user</button>
              </form>
            </div>

            <div className="user-card list-card">
              <h2>Team members</h2>
              {status?.message && (
                <div className={`notice popup ${status.type}`}>
                  <div className="notice-content">
                    <span className="notice-dot" aria-hidden />
                    <div>
                      <div className="notice-title">{status.type === 'success' ? 'All set' : 'Heads up'}</div>
                      <div className="notice-text">{status.message}</div>
                    </div>
                  </div>
                  <button
                    className="notice-dismiss"
                    type="button"
                    aria-label="Dismiss"
                    onClick={() => setStatus(null)}
                  >
                    ×
                  </button>
                </div>
              )}
              <div className="user-list">
                {users.map(u => (
                  <div className="user-row" key={u._id}>
                    <div>
                      <div className="user-name">{u.displayName}</div>
                      <div className="user-email">{u.email}</div>
                    </div>
                    <div className="user-actions">
                      {u._id !== selfId && (
                        <>
                          <select
                            value={pendingRole[u._id] ?? u.role}
                            onChange={(e) => handleRoleSelectChange(u._id, e.target.value)}
                            className="role-select"
                          >
                            <option value="normal">Normal</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            className="ghost-btn danger"
                            onClick={() => handleDelete(u._id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {!users.length && <div className="empty-state">No users yet.</div>}
              </div>
              {users.length > 0 && Object.entries(pendingRole).some(([id, val]) => {
                const current = users.find(u => u._id === id);
                return current && current._id !== selfId && current.role !== val;
              }) && (
                <div className="bulk-actions">
                  <button
                    className="save-all-btn"
                    onClick={async () => {
                      const changes = users
                        .filter(u => u._id !== selfId && pendingRole[u._id] && pendingRole[u._id] !== u.role)
                        .map(u => ({ id: u._id, role: pendingRole[u._id] }));
                      if (!changes.length) return;
                      try {
                        setStatus(null);
                        await Promise.all(changes.map(c => handleRoleChange(c.id, c.role)));
                        setStatus({ type: 'success', message: 'Changes saved' });
                        setPendingRole({});
                        await refreshUsers();
                      } catch {
                        // status already set in handler
                      }
                    }}
                  >
                    Save changes
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAdmin;
